import React, { forwardRef, useImperativeHandle } from 'react';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow as XYFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import {
  type ReactFlowProps,
  type ReactFlowHandle,
  type CanvasConfig,
  defaultBackgroundConfig,
  defaultMiniMapConfig,
  defaultControlsConfig,
  defaultCanvasConfig,
  editableCanvasKeys,
} from './reactFlow.types';
import styled from '@emotion/styled';

/**
 * Higher-order component that wraps a custom node component and adds
 * source/target handles automatically. This ensures edges can connect
 * to custom nodes without requiring manual Handle components.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withHandles(NodeComponent: React.ComponentType<any>): React.FC<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const WrappedNode: React.FC<any> = (props) => (
    <div className="relative">
      <Handle
        type="target"
        position={Position.Top}
        style={{ visibility: 'hidden' }}
      />
      <NodeComponent {...props} />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ visibility: 'hidden' }}
      />
    </div>
  );

  WrappedNode.displayName = `WithHandles(${NodeComponent.displayName || NodeComponent.name || 'Component'})`;
  return WrappedNode;
}

// Re-export types from types file
export type {
  ReactFlowProps,
  ReactFlowHandle,
  BackgroundConfig,
  MiniMapConfig,
  ControlsConfig,
  CanvasConfig,
} from './reactFlow.types';

type ReactFlowInnerProps = ReactFlowProps & {
  innerRef?: React.RefObject<ReactFlowHandle>;
};

const StyledXYFlow = styled(XYFlow)`
  .react-flow__attribution {
    display: none;
  }
`;

const ReactFlowInner: React.FC<ReactFlowInnerProps> = ({
  // Config objects (need special handling)
  backgroundConfig,
  miniMapConfig,
  controlsConfig,
  canvasConfig,

  // Custom props (not passed to XYFlow)
  readOnly,
  showBackground = true,
  children,
  innerRef,

  // Props that need conditional handling when readOnly
  onConnect,
  onConnectStart,
  onConnectEnd,
  onNodesDelete,
  onEdgesDelete,

  // Rest goes directly to XYFlow
  ...rest
}) => {
  const instance = useReactFlow();

  // Expose imperative methods via ref
  useImperativeHandle(innerRef, () => {
    return { instance };
  }, [instance]);

  // Merge configs with defaults
  const bgConfig = { ...defaultBackgroundConfig, ...backgroundConfig };
  const mmConfig = { ...defaultMiniMapConfig, ...miniMapConfig };
  const ctrlConfig = { ...defaultControlsConfig, ...controlsConfig };
  const cvConfig = { ...defaultCanvasConfig, ...canvasConfig };

  // When readOnly, override editable-related canvas config keys
  const effectiveCanvasConfig: CanvasConfig = readOnly
    ? {
        ...cvConfig,
        ...Object.fromEntries(editableCanvasKeys.map((key) => [key, false])),
      }
    : cvConfig;

  return (
    <StyledXYFlow
      {...rest}
      {...effectiveCanvasConfig}
      onConnect={readOnly ? undefined : onConnect}
      onConnectStart={readOnly ? undefined : onConnectStart}
      onConnectEnd={readOnly ? undefined : onConnectEnd}
      onNodesDelete={readOnly ? undefined : onNodesDelete}
      onEdgesDelete={readOnly ? undefined : onEdgesDelete}
    >
      {showBackground && <Background {...bgConfig} />}

      {mmConfig.show && <MiniMap {...mmConfig} />}

      {ctrlConfig.show && <Controls {...ctrlConfig} />}

      {children}
    </StyledXYFlow>
  );
};

/**
 * A fully controlled React Flow component.
 *
 * @example
 * ```tsx
 * import { useNodesState, useEdgesState, addEdge } from '@xyflow/react';
 * import { ReactFlow, ReactFlowHandle } from 'src/components';
 *
 * const MyFlow = () => {
 *   const flowRef = useRef<ReactFlowHandle>(null);
 *   const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
 *   const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
 *
 *   const onConnect = useCallback(
 *     (params) => setEdges((eds) => addEdge(params, eds)),
 *     [setEdges]
 *   );
 *
 *   // Imperative control via ref
 *   const handleFitView = () => {
 *     flowRef.current?.fitView({ padding: 0.5, duration: 200 });
 *   };
 *
 *   return (
 *     <ReactFlow
 *       ref={flowRef}
 *       nodes={nodes}
 *       edges={edges}
 *       onNodesChange={onNodesChange}
 *       onEdgesChange={onEdgesChange}
 *       onConnect={onConnect}
 *       readOnly={false}
 *       showBackground={true}
 *       miniMapConfig={{ show: true }}
 *       controlsConfig={{ show: true }}
 *     />
 *   );
 * };
 * ```
 */
export const ReactFlow = forwardRef<ReactFlowHandle, ReactFlowProps>(
  (props, ref) => (
    <ReactFlowProvider>
      <ReactFlowInner
        {...props}
        innerRef={ref as React.RefObject<ReactFlowHandle>}
      />
    </ReactFlowProvider>
  )
);

ReactFlow.displayName = 'ReactFlow';

// Re-export useful types and utilities from @xyflow/react for convenience
export {
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type NodeTypes,
  type EdgeTypes,
  type NodeProps,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from '@xyflow/react';

// Export the withHandles HOC for advanced use cases where consumers
// need more control over handle placement
export { withHandles };
